import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Icon } from "semantic-ui-react";
import { useNavigate, useParams } from "react-router-dom";

import { Schema } from "../../../../../../api";

import {
  initialValues,
  validationSchema,
} from "./EditSchemaPage.form";

import styles from "./EditSchemaPage.module.scss";

const schemaCtrl = new Schema();

export function EditSchemaPage() {
  const navigate = useNavigate();
  const { schemaId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [serverError, setServerError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,

    onSubmit: async (values) => {
      try {
        setServerError(null);

        const schemaData = {
          id: values.id.trim().toUpperCase(),
          name: values.name.trim(),
          description: values.description.trim(),
          order: Number(values.order),
        };

        await schemaCtrl.updateSchema(schemaId, schemaData);

        navigate("/admin/catalog/schema", {
          state: {
            success: "Esquema actualizado correctamente.",
          },
        });
      } catch (error) {
        console.error("Error al actualizar esquema:", error);

        setServerError(
          error?.msg ||
            "No fue posible actualizar el esquema. Intenta nuevamente."
        );

        if (error?.errors) {
          formik.setErrors(error.errors);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
  });

  useEffect(() => {
    async function loadSchema() {
      try {
        setIsLoading(true);
        setServerError(null);

        const result = await schemaCtrl.getSchema(schemaId);
        const schema = result.schema;

        if (!schema) {
          throw new Error("No se encontró el esquema solicitado.");
        }

        formik.setValues({
          id: schema.id || "",
          name: schema.name || "",
          description: schema.description || "",
          order: schema.order || "",
        });
      } catch (error) {
        console.error("Error al obtener esquema:", error);

        setServerError(
          error?.msg ||
            "No fue posible cargar el esquema. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadSchema();
  }, [schemaId]);

  function getFieldError(fieldName) {
    const shouldShowError =
      (hasSubmitted || formik.touched[fieldName]) &&
      formik.errors[fieldName];

    return shouldShowError ? formik.errors[fieldName] : null;
  }

  function handleCancel() {
    if (formik.isSubmitting) {
      return;
    }

    navigate("/admin/catalog/schema");
  }

  function handleInputChange(event) {
    const { name, value } = event.target;

    formik.setFieldValue(name, value);
    setServerError(null);

    if (hasSubmitted) {
      formik.validateField(name);
    }
  }

  function handleInputBlur(event) {
    formik.handleBlur(event);

    if (hasSubmitted) {
      formik.validateField(event.target.name);
    }
  }

  async function handleSave() {
    setHasSubmitted(true);

    formik.setTouched(
      {
        id: true,
        name: true,
        description: true,
        order: true,
      },
      false
    );

    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    formik.handleSubmit();
  }

  if (isLoading) {
    return (
      <section className={styles.EditSchemaPage}>
        <div className={styles.StateMessage}>
          Cargando información del esquema...
        </div>
      </section>
    );
  }

  return (
    <section className={styles.EditSchemaPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={formik.isSubmitting}
            aria-label="Volver al listado de esquemas"
          >
            <Icon name="arrow left" />
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Catálogo / Esquemas / Editar esquema
            </p>

            <h1>Editar esquema</h1>

            <p className={styles.Subtitle}>
              Actualiza la información del esquema.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={formik.isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={styles.PublishButton}
            onClick={handleSave}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting
              ? "Guardando..."
              : "Guardar cambios"}
          </button>
        </div>
      </header>

      {serverError && (
        <div className={styles.ServerErrorMessage}>
          <Icon name="warning sign" />
          <span>{serverError}</span>
        </div>
      )}

      {hasSubmitted && Object.keys(formik.errors).length > 0 && (
        <div className={styles.ValidationMessage}>
          <Icon name="warning sign" />

          <div>
            <strong>Revisa la información del esquema.</strong>

            <span>
              Debes corregir los campos marcados antes de guardar.
            </span>
          </div>
        </div>
      )}

      <form
        className={styles.FormContent}
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <section className={styles.SchemaBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información del esquema</h2>

              <p>
                Actualiza el catálogo padre que agrupa sus clasificaciones.
              </p>
            </div>
          </header>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label htmlFor="id">
                Código del esquema <span>*</span>
              </label>

              <input
                id="id"
                name="id"
                type="text"
                value={formik.values.id}
                placeholder="Ej. TIPO_PRODUCTO"
                className={
                  getFieldError("id") ? styles.InputError : ""
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={formik.isSubmitting}
              />

              {getFieldError("id") && (
                <span className={styles.FieldError}>
                  {getFieldError("id")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden de visualización <span>*</span>
              </label>

              <input
                id="order"
                name="order"
                type="number"
                min="1"
                value={formik.values.order}
                placeholder="Ej. 1"
                className={
                  getFieldError("order") ? styles.InputError : ""
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={formik.isSubmitting}
              />

              {getFieldError("order") && (
                <span className={styles.FieldError}>
                  {getFieldError("order")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="name">
                Nombre del esquema <span>*</span>
              </label>

              <input
                id="name"
                name="name"
                type="text"
                value={formik.values.name}
                placeholder="Ej. Tipo de producto"
                className={
                  getFieldError("name") ? styles.InputError : ""
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={formik.isSubmitting}
              />

              {getFieldError("name") && (
                <span className={styles.FieldError}>
                  {getFieldError("name")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <textarea
                id="description"
                name="description"
                rows="5"
                value={formik.values.description}
                placeholder="Describe el propósito del esquema..."
                className={
                  getFieldError("description")
                    ? styles.InputError
                    : ""
                }
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={formik.isSubmitting}
              />

              <div className={styles.TextAreaFooter}>
                {getFieldError("description") ? (
                  <span className={styles.FieldError}>
                    {getFieldError("description")}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formik.values.description.length}/500</span>
              </div>
            </div>
          </div>
        </section>
      </form>
    </section>
  );
}